import {
	type ActionFromReducersMapObject,
	combineReducers,
	type Reducer,
	type ReducersMapObject,
} from 'redux';

import type { AsyncActionStatus } from '../actions/common.actionTypes';
import aiAssistant, { type AIAssistantState } from './aiAssistant.reducers';
import aiFilterValues, { type AiFilterValuesState } from './aiFilterValues.reducers';
import collections, { type CollectionsInitialStateType } from './collections.reducers';
import corrections, { type CorrectionsState } from './corrections.reducers';
import courses, { type CourseState } from './courses.reducers';
import dashboard, { type DashboardInitialStateType } from './dashboard.reducers';
import download, { type DownloadInitialStateType } from './download.reducers';
import errors, { type ErrorsState } from './errors.reducers';
import licenceModels, { type LicenceModelsStateType } from './licenceModels.reducers';
import mindItems, { type MindItemState } from './mindItems.reducers';
import pinned, { type PinnedInitialStateType } from './pinned.reducers';
import products, { type ProductState } from './products.reducers';
import publishers, { type PublisherState } from './publishers.reducers';
import recentlyViewed, { type RecentlyViewedInitialStateType } from './recentlyViewed.reducers';
import schoolTypes, { type SchoolTypeState } from './schoolTypes.reducers';
import search, { type SearchInitialStateType } from './search.reducers';
import stripeSchoolAdmins, { type StripeSchoolAdminsState } from './stripeSchoolAdmins.reducers';
import stripeSchoolTeachers, {
	type StripeSchoolTeachersState,
} from './stripeSchoolTeachers.reducers';
import user, { type UserInitialStateType } from './user.reducers';

export type StoreItem<T> = {
	data?: T;
	status: AsyncActionStatus;
	error?: string;
};

export interface StoreType {
	collections: CollectionsInitialStateType;
	corrections: CorrectionsState;
	courses: CourseState;
	dashboard: DashboardInitialStateType;
	download: DownloadInitialStateType;
	errors: ErrorsState;
	licenceModels: LicenceModelsStateType;
	mindItems: MindItemState;
	pinned: PinnedInitialStateType;
	products: ProductState;
	recentlyViewed: RecentlyViewedInitialStateType;
	publishers: PublisherState;
	schoolTypes: SchoolTypeState;
	aiFilterValues: AiFilterValuesState;
	search: SearchInitialStateType;
	user: UserInitialStateType;
	aiAssistant: AIAssistantState;
	stripeSchoolTeachers: StripeSchoolTeachersState;
	stripeSchoolAdmins: StripeSchoolAdminsState;
}

const reducers = {
	collections,
	corrections,
	courses,
	dashboard,
	download,
	errors,
	licenceModels,
	mindItems,
	pinned,
	products,
	recentlyViewed,
	schoolTypes,
	aiFilterValues,
	search,
	publishers,
	user,
	aiAssistant,
	stripeSchoolTeachers,
	stripeSchoolAdmins,
	// StoreType is hand-written (the render helpers and AppDispatch build on it);
	// `satisfies` fails when a slice is missing, extra, or typed differently.
	// Each slice keeps its own action union, hence the open action type.
} satisfies ReducersMapObject<StoreType, any>;

// redux 5 derives the preloaded-state shape from each slice reducer's first
// parameter, which the slices' narrow action unions turn into `never` per key;
// the assertion restores the partial shape callers seed a store with.
const rootReducer = combineReducers(reducers) as Reducer<
	StoreType,
	ActionFromReducersMapObject<typeof reducers>,
	DeepPartial<StoreType>
>;

export default rootReducer;
